import { Button, Card, Divider, Empty, Form, Input, Modal, ModalProps, Row, Skeleton, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { useForm } from 'antd/es/form/Form';
import AskMindSparkButtonIcon from 'assets/AskMindSparkButtonIcon.svg';
import { nanoid } from 'nanoid';
import { retrieveFMVMinerData } from 'api/generative_ai';
import SelectReusable from '../shared/SelectReusable';
import { vendorList } from 'sb/shared/constants';
import Markdown from 'react-markdown';
import { Col } from 'antd/es';

interface Props extends ModalProps {
  closeModal: () => void;
}

export const AIMindSparkChatbotModal: React.FC<Props> = ({ closeModal, ...rest }: Props) => {
  const [form] = useForm();
  const [selectedVendor, setSelectedVendor] = useState<string | undefined>(undefined);
  const [enteredPrompt, setEnteredPrompt] = useState<string>('');
  const [selectedDealType, setSelectedDealType] = useState<string | undefined>(undefined);
  const [searching, setSearching] = useState(false);
  const [aiReponse, setAiReponse] = useState<string | undefined>(undefined);
  const sessionId = nanoid();

  const handleVendorChange = (value: string) => {
    setSelectedVendor(value);
  };

  const handleDealTypeChange = (value: string) => {
    setSelectedDealType(value);
  };

  const handlePromptChange = (prompt: any) => {
    setEnteredPrompt(prompt);
  };

  useEffect(() => {
    if (rest.open) {
      setAiReponse(undefined);
      setSelectedVendor(undefined);
      setSelectedDealType(undefined);
      form.resetFields();
      form.setFieldsValue({
        vendor: undefined,
        prompt: undefined
      });
    }
  }, [form, rest.open]);

  const getTitle = () => {
    return (
      <Space>
        <img src={AskMindSparkButtonIcon} alt="Icon" />
        <span>FMV Miner</span>
      </Space>
    );
  };

  const getVendorList = () => {
    return vendorList.map((item) => {
      return { label: item, value: item };
    });
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch (e) {
      console.log(e);
      return;
    }

    try {
      setSearching(true);
      const res = await retrieveFMVMinerData({
        query: enteredPrompt,
        session_id: sessionId,
        vendor: selectedVendor,
        deal_type: selectedDealType
      });
      setAiReponse(res);
    } catch (e) {
      console.log(e);
    } finally {
      setSearching(false);
    }
  };

  const dealTypesOptions = [
    { label: 'Renewal', value: 'Renewal' },
    { label: 'New Purchase', value: 'New Purchase' }
  ];

  return (
    <Modal
      width={750}
      title={getTitle()}
      {...rest}
      onCancel={closeModal}
      footer={[
        <Button
          key="submit"
          type="primary"
          loading={searching}
          onClick={handleSubmit}
          disabled={!(enteredPrompt?.trim()?.length > 0) || !enteredPrompt}
        >
          Submit
        </Button>
      ]}
    >
      <Card className="mb-2 h-64 max-h-64 pt-2" bordered={true} style={{ height: 260, overflowY: 'auto' }}>
        <div>
          {aiReponse && <Markdown>{aiReponse}</Markdown>}
          {!aiReponse && searching && <Skeleton loading={searching} active paragraph={{ rows: 5 }} />}
          {!aiReponse && !searching && <Empty />}
        </div>
      </Card>
      <Divider />
      <Form form={form} initialValues={{ prompt: enteredPrompt }}>
        <Row justify="space-between">
          <Col>
            <Form.Item name="vendor" rules={[{ required: true, message: 'Supplier required' }]}>
              <SelectReusable
                style={{ width: 300 }}
                placeholder="Select Supplier"
                options={getVendorList()}
                showSearch={true}
                allowClear={true}
                onChange={handleVendorChange}
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item name="dealType" rules={[{ required: true, message: 'Deal type required' }]}>
              <SelectReusable
                style={{ width: 300 }}
                placeholder="Select Deal Type"
                options={dealTypesOptions}
                showSearch={true}
                allowClear={true}
                onChange={handleDealTypeChange}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="prompt">
          <Input.TextArea
            placeholder="E.g. What type of discount should I expect from when purchasing 30,000 seats of Falcon Endpoint Protection?"
            rows={3}
            onChange={(e) => handlePromptChange(e?.target?.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
